/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2017 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

import React from 'react';
import PropTypes from 'prop-types';
import { IntlProvider, injectIntl } from 'react-intl';
import EventProvider from '../../providers/EventProvider';
import { version } from '../../../../package.json';
import Utilities from '../../util/Utilities';
import icons from '../../icons';
import style from '../../Themes/PearsonLightTheme';
import './navigation.scss';
import messages from '../../defaultMessages';
import msgObject from '../../../../translations';
import IntlSupport from '../Localization/InternationalSupport';

/**
 * A React component for the prev/next navigation.
 *
 * @author Hari Gangadharan
 */
class Navigation extends React.Component {
  navInterval = 0;

  constructor(props) {
    super(props);
    this.state = {
      pageIndex: 0
    };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyPress);
    /* eslint-enable */
    this.determinePageIndex(this.props.currentPageId, this.props.pagePlayList);
  }

  componentWillReceiveProps(newProps) {
    if (this.props.currentPageId !== newProps.currentPageId || this.props.pagePlayList !== newProps.pagePlayList) {
      this.determinePageIndex(newProps.currentPageId, newProps.pagePlayList);
    }
  }

  componentWillUnmount() {
    if (this.navInterval !== 0) {
      clearInterval(this.navInterval);
      this.navInterval = 0;
    }
    window.removeEventListener('keydown', this.handleKeyPress);
  }


  modelOpen = () => (document.body.classList.contains('ReactModal__Body--open'))

  handleKeyPress = (event) => {
    if (event && event.target && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      const targetElement = event.target.tagName || '';
      if (targetElement && targetElement !== 'TEXTAREA' && targetElement !== 'INPUT' && !this.modelOpen()) {
        this.navigatePage((event.key === 'ArrowRight'));
      }
    }
  };

  handleSectionClick = (event, isNext) => {
    this.navigatePage(isNext);
    event.target.parentNode.blur();
  };

  navigatePage = (isNext) => {
    let newIndex = this.state.pageIndex;
    if (isNext) {
      newIndex += 1;
    } else {
      newIndex -= 1;
    }
    if (newIndex < 0 || newIndex >= this.props.pagePlayList.length) {
      return; // don nothing - out of bounds
    }
    const pageRequested = this.props.pagePlayList[newIndex];
    this.props.onPageRequest(pageRequested);
    window.scroll(0, 0);
    EventProvider.getEventManager().dispatch({
      category: 'Navigation',
      action: isNext ? 'Next' : 'Prev',
      data: { pageRequested },
      contentId: newIndex,
      componentVersion: version
    });
  };

  getFormatedTitle = title => (Utilities.textFromContent(title))

  isFirstPage() {
    return (this.state.pageIndex === 0);
  }

  isLastPage() {
    return (this.props.pagePlayList && this.state.pageIndex === this.props.pagePlayList.length - 1);
  }

  determinePageIndex(id, pagePlayList) {
    if (id) {
      const index = pagePlayList.findIndex(node => (node.id === id));
      if (index >= 0) {
        this.setState({ pageIndex: index });
      }
    }
  }

  render() {
    const intlObj = new IntlSupport(msgObject, this.props.intl.locale);
    const { formatMessage } = this.props.intl;
    const { Next, Prev } = icons;
    const playerNav = this.props.player ? 'nav-up' : '';
    const prev = this.isFirstPage() ? null : (
      <div
        tabIndex="0"
        role="presentation"
        aria-label={formatMessage(messages.previous)}
        className="contentWrapper"
        title={this.props.pagePlayList[this.state.pageIndex - 1].title}
        onClick={event => this.handleSectionClick(event, false)}
        onKeyPress={() => this.navigatePage(false)}
      >
        <Prev viewBox="24 28 18 9" style={style.buttons.prevBtn} />
        <span className="content">
          {this.getFormatedTitle(this.props.pagePlayList[this.state.pageIndex - 1].title)}
        </span>
      </div>
    );
    const next = this.isLastPage() ? null : (
      <div
        tabIndex="0"
        role="presentation"
        aria-label={formatMessage(messages.next)}
        className="contentWrapper"
        title={this.props.pagePlayList[this.state.pageIndex + 1].title}
        onClick={event => this.handleSectionClick(event, true)}
        onKeyPress={() => this.navigatePage(true)}
      >
        <span className="content">
          {this.getFormatedTitle(this.props.pagePlayList[this.state.pageIndex + 1].title)}
        </span>
        <Next viewBox="1209 28 18 9" style={style.buttons.nextBtn} />
      </div>
    );
    const finalElement = (
      <div className={this.props.hide ? 'navigation nav-down' : `navigation ${playerNav}`}>
        <div className="section prev">
          {prev}
        </div>
        <div className="separator" />
        <div className="section next">
          {next}
        </div>
      </div>
    );

    return (
      <IntlProvider locale={intlObj.getLocale()} messages={intlObj.getMessages()}>
        {finalElement}
      </IntlProvider>
    );
  }
}

Navigation.defaultProps = {
  hide: false,
  player: false
};

Navigation.propTypes = {
  intl: PropTypes.object,
  hide: PropTypes.bool,
  onPageRequest: PropTypes.func.isRequired,
  pagePlayList: PropTypes.arrayOf(PropTypes.object),
  currentPageId: PropTypes.string.isRequired,
  player: PropTypes.bool
};

export default injectIntl(Navigation);



// WEBPACK FOOTER //
// ./src/js/components/Navigation/Navigation.jsx